import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X } from 'lucide-react';

export default function Header({ onLoginClick }) {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isHome = location.pathname === '/';
  const navLinks = [
    { href: '#home', label: 'Home' },
    { href: '#ecosystem', label: 'Ecosystem' },
    { href: '#about', label: 'About' },
    { href: '#path', label: 'Path' },
    { href: '/team', label: 'Team' },
    { href: '/verify-certificate', label: 'Verify Certificate' },
    { href: '#faq', label: 'FAQ' },
  ];

  const handleNavClick = (href) => {
    setMobileMenuOpen(false); // Close mobile menu on click
    if (href.startsWith('/')) {
      navigate(href);
      return;
    }
    if (!isHome) {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(href.replace('#', ''));
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    } else {
      const el = document.getElementById(href.replace('#', ''));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Close mobile menu if window resizes to desktop width
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <header className="header-fixed">
      <nav className="pill-nav" style={{ flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', maxWidth: '100%' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
            <div className="logo-container" style={{ transition: 'transform 0.2s' }} onMouseOver={e => e.currentTarget.style.transform = 'scale(1.1)'} onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}>
              <img src="/Logo.jpeg" alt="EKALAVYA Logo" className="logo-img" />
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="md-nav" style={{ display: 'flex', alignItems: 'center' }}>
            <ul style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', listStyle: 'none' }}>
              {navLinks.map(link => (
                <li key={link.href}>
                  <button className="nav-pill" onClick={() => handleNavClick(link.href)}>{link.label}</button>
                </li>
              ))}
            </ul>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', borderLeft: '1.5px solid #e2e8f0', paddingLeft: '1rem', marginLeft: '0.5rem' }}>
              {user ? (
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.875rem', background: 'linear-gradient(135deg, var(--primary-gold) 0%, var(--primary-gold-deep) 100%)', border: 'none', borderRadius: '9999px', color: 'white', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', transition: 'all 0.3s', boxShadow: '0 2px 8px var(--gold-shadow)' }}
                  >
                    <img
                      src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${profile?.full_name || user.id}`}
                      alt="Avatar"
                      style={{ width: '1.5rem', height: '1.5rem', borderRadius: '50%', background: 'rgba(255,255,255,0.2)' }}
                    />
                    {profile?.full_name?.split(' ')[0] || 'Scholar'}
                  </button>
                  {showUserMenu && (
                    <div style={{ position: 'absolute', right: 0, top: '110%', background: 'white', border: '1px solid #e2e8f0', borderRadius: '1rem', padding: '0.5rem', minWidth: '160px', zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                      <Link to={user.role === 'mentor' ? '/mentor-dashboard' : '/dashboard'} onClick={() => setShowUserMenu(false)} style={{ display: 'block', padding: '0.625rem 1rem', color: '#475569', fontSize: '0.85rem', borderRadius: '0.5rem', transition: 'all 0.2s' }} onMouseOver={e => e.currentTarget.style.background = '#f1f5f9'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>Dashboard</Link>
                      <Link to="/profile" onClick={() => setShowUserMenu(false)} style={{ display: 'block', padding: '0.625rem 1rem', color: '#475569', fontSize: '0.85rem', borderRadius: '0.5rem', transition: 'all 0.2s' }} onMouseOver={e => e.currentTarget.style.background = '#f1f5f9'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>Profile</Link>
                      <button onClick={() => { signOut(); setShowUserMenu(false); navigate('/'); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.625rem 1rem', color: '#ef4444', fontSize: '0.85rem', borderRadius: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>Sign Out</button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <button className="nav-pill-outline" onClick={onLoginClick}>Login</button>
                  <button className="nav-pill-primary" onClick={onLoginClick}>Join Now</button>
                </>
              )}
            </div>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="mobile-menu-btn">
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{ background: 'transparent', border: 'none', color: 'var(--navy-dark)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Nav */}
        {mobileMenuOpen && (
          <div className="mobile-nav" style={{ width: '100%', padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '0.5rem', borderTop: '1px solid #e2e8f0', marginTop: '1rem', animation: 'fadeIn 0.2s ease-in-out' }}>
            <ul style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', listStyle: 'none', padding: 0, margin: 0 }}>
              {navLinks.map(link => (
                <li key={link.href} style={{ width: '100%' }}>
                  <button 
                    className="nav-pill" 
                    onClick={() => handleNavClick(link.href)}
                    style={{ width: '100%', textAlign: 'left', padding: '0.75rem 1rem', fontSize: '1rem' }}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
              {user ? (
                <>
                  <button
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', background: 'rgba(147, 51, 234, 0.08)', border: 'none', borderRadius: '0.75rem', color: 'var(--primary-gold)', fontWeight: 600, fontSize: '1rem', cursor: 'default' }}
                  >
                    <img
                      src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${profile?.full_name || user.id}`}
                      alt="Avatar"
                      style={{ width: '1.75rem', height: '1.75rem', borderRadius: '50%' }}
                    />
                    {profile?.full_name || 'Scholar'}
                  </button>
                  <Link to={user.role === 'mentor' ? '/mentor-dashboard' : '/dashboard'} onClick={() => setMobileMenuOpen(false)} style={{ display: 'block', padding: '0.75rem 1rem', color: '#475569', fontSize: '1rem', borderRadius: '0.5rem', fontWeight: 500 }}>Dashboard</Link>
                  <Link to="/profile" onClick={() => setMobileMenuOpen(false)} style={{ display: 'block', padding: '0.75rem 1rem', color: '#475569', fontSize: '1rem', borderRadius: '0.5rem', fontWeight: 500 }}>Profile</Link>
                  <button onClick={() => { signOut(); setMobileMenuOpen(false); navigate('/'); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem 1rem', color: '#ef4444', fontSize: '1rem', borderRadius: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }}>Sign Out</button>
                </>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem', width: '100%', padding: '0 1rem' }}>
                  <button className="nav-pill-outline" style={{ flex: 1, padding: '0.75rem' }} onClick={() => { setMobileMenuOpen(false); onLoginClick(); }}>Login</button>
                  <button className="nav-pill-primary" style={{ flex: 1, padding: '0.75rem' }} onClick={() => { setMobileMenuOpen(false); onLoginClick(); }}>Join Now</button>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}

